import React from 'react';
import styled from 'styled-components';
import { Icon } from '../elements/Icon';
import { ICON_OPEN_OUTSIDE } from '../refs/Icons';
import { getColor } from '../utils/styled';
import { GRAY_20, GRAY_120, MINE_SHAFT, LOCHMARA, LOCHMARA_SHADOW, TOREA_BAY } from '../refs/Colors';

type PatientAndStudyProps = {
  className?: string;
  patientName: string;
  studies: {title: string; date?: string; url?: string, disabled: boolean}[];
};

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  max-width: 210mm;
  height: 60px;
  padding: 14px 0;
  box-sizing: border-box;
  background-color: ${getColor(GRAY_20)};
  margin: 0 auto;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LabelText = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: normal;
  color: ${getColor(GRAY_120)};
  margin: 0;
`;

const PatientName = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: normal;
  color: ${getColor(MINE_SHAFT)};
  margin: 0;
`;

const StudyRow = styled.a<{$disabled: boolean}>`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 6px;
  text-decoration: none;
  ${props => props.$disabled && `
    cursor: default !important;
    opacity: 0.4 !important;
    pointer-events: none;
  `}
`;

const StudyLink = styled.span`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: normal;
  color: ${getColor(LOCHMARA_SHADOW)};
  text-decoration-line: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
  text-decoration-thickness: 1px;
  text-decoration-skip-ink: none;
  cursor: pointer;
  transition-property: color, text-decoration-color;
  transition-duration: 0.3s;

  &:hover {
    color: ${getColor(LOCHMARA)};
    text-decoration-color: ${getColor(LOCHMARA)};
  }

  &:active {
    color: ${getColor(TOREA_BAY)};
    text-decoration-color: ${getColor(TOREA_BAY)};
  }
`;

const StudyTitle = styled.span`
  font-weight: 600;
`;

const StudyDate = styled.span`
  font-weight: 400;
`;


export const PatientAndStudy: React.FC<PatientAndStudyProps> = ({
  className,
  patientName,
  studies
}) => {

  return (
    <Container className={className}>
      <Column>
        <LabelText>Patient:</LabelText>
        <PatientName>{patientName}</PatientName>
      </Column>
      <Column>
        <LabelText>{studies.length > 1 ? "Studies: " : "Study: "}</LabelText>
        {studies.map(study =>
          <StudyRow $disabled={study.disabled} as={study.url && !study.disabled ? 'a' : 'div'} {...(study.url && !study.disabled ? {href: study.url, target: '_blank', rel: 'noreferrer'} : {})}>
            <StudyLink>
              <StudyTitle>{study.title}</StudyTitle>
              {study.date ? <StudyDate> ({study.date})</StudyDate> : null}
            </StudyLink>
            <Icon className="study-link-ic" color={LOCHMARA} icon={ICON_OPEN_OUTSIDE} size={16}/>
          </StudyRow>
        )}
      </Column>
    </Container>
  );
};

export default PatientAndStudy;
